import React, { useMemo, useState, useEffect, useRef } from 'react'
import Select from 'react-select'
import { People } from '../../../../types/People'
import { SelectOption } from '../../../../types/SelectOption'
import {
    GroupConnexionState,
    CompanyOverrideState,
    SiteOverrideState,
    CompanyCreditsBdc,
} from '../hooks/useGroupSignatureForm'

interface GroupConnectionsSectionProps {
    groupConnexion: GroupConnexionState
    onUpdateGroupConnexion: (field: keyof GroupConnexionState, value: any) => void
    onTestGroupConnexion: () => Promise<void>
    testingGroup: boolean

    contreSignatureGroupe: boolean
    onToggleContreSignatureGroupe: () => void

    applyConnectionToAll: boolean
    applyCounterSignToAll: boolean
    onToggleApplyConnectionToAll: () => void
    onToggleApplyCounterSignToAll: () => void

    companyOverrides: CompanyOverrideState[]
    onUpdateSiteOverride: (idPlaque: number, idSite: number, field: keyof SiteOverrideState, value: any) => void
    onTestSiteConnection: (idPlaque: number, idSite: number) => Promise<void>
    testingSite: string | null
    onUpdateCompanyCounterSign: (idPlaque: number, value: boolean | null) => void

    creditsByBdc: Record<number, CompanyCreditsBdc>
    onFilterCreditsByBdc: (idPlaque: number, noCmd: string) => void
    contrats: Array<{ NoCmd: string }>

    users: People[]
    getConnectionToggleClass: (status?: 'inactive' | 'active' | 'error') => string
}

export default function GroupConnectionsSection({
    groupConnexion,
    onUpdateGroupConnexion,
    onTestGroupConnexion,
    testingGroup,
    contreSignatureGroupe,
    onToggleContreSignatureGroupe,
    applyConnectionToAll,
    applyCounterSignToAll,
    onToggleApplyConnectionToAll,
    onToggleApplyCounterSignToAll,
    companyOverrides,
    onUpdateSiteOverride,
    onTestSiteConnection,
    testingSite,
    onUpdateCompanyCounterSign,
    creditsByBdc,
    onFilterCreditsByBdc,
    contrats,
    users,
    getConnectionToggleClass,
}: GroupConnectionsSectionProps) {
    const [showBdcDropdown, setShowBdcDropdown] = useState(false)
    const [selectedBdc, setSelectedBdc] = useState('')
    const bdcDropdownRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!showBdcDropdown) return
        const handleClickOutside = (e: MouseEvent) => {
            if (bdcDropdownRef.current && !bdcDropdownRef.current.contains(e.target as Node)) {
                setShowBdcDropdown(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [showBdcDropdown])

    const userOptions: SelectOption[] = useMemo(() =>
        users.map(user => ({
            label: user.FullName || `${user.Lastname} ${user.Firstname.charAt(0).toUpperCase() + user.Firstname.slice(1).toLowerCase()}`,
            value: user.IDPeople
        }))
    , [users])

    const getUserOption = (idPeople: number | undefined): SelectOption | null => {
        if (!idPeople) return null
        return userOptions.find(opt => opt.value === idPeople) || null
    }

    const groupCanTest = !!(groupConnexion.IDPeopleRepresentant && groupConnexion.Token)
    const groupIsActive = !!(groupConnexion.EstActif && groupConnexion.IDPeopleRepresentant && groupConnexion.Token)

    // Le mode « appliquer à tous » pour la connexion n'a de sens que si un paramétrage global est réellement actif.
    // Sans paramétrage global actif, on doit pouvoir paramétrer chaque site individuellement.
    const effectiveApplyConnectionToAll = applyConnectionToAll && groupIsActive

    const renderCounterSignButton = (company: CompanyOverrideState) => {
        if (applyCounterSignToAll) {
            // Mode groupe : la valeur de la contre-signature groupe s'applique à toutes les sociétés.
            return (
                <button className={`btn btn-sm ${contreSignatureGroupe ? 'btn-success' : 'btn-warning'}`} disabled>
                    <i className={`fa ${contreSignatureGroupe ? 'fa-toggle-on' : 'fa-toggle-off'} mr-1`}></i>
                    {contreSignatureGroupe ? 'Activée' : 'Désactivée'}
                </button>
            )
        }

        // Mode par société : valeur propre à la société (null → valeur du groupe par défaut).
        const isOn = company.ContreSignatureAutomatique === null
            ? contreSignatureGroupe
            : company.ContreSignatureAutomatique
        return (
            <button
                className={`btn btn-sm ${isOn ? 'btn-success' : 'btn-warning'}`}
                onClick={() => onUpdateCompanyCounterSign(company.IDPlaque, !isOn)}
            >
                <i className={`fa ${isOn ? 'fa-toggle-on' : 'fa-toggle-off'} mr-1`}></i>
                {isOn ? 'Activée' : 'Désactivée'}
            </button>
        )
    }

    return (
        <div className="card mb-4">
            <div className="card-header">
                <h4>Connexion à Oodrive, activation de la contre-signature et suivi de la consommation de crédits par société</h4>
            </div>
            <div className="card-body">

                {/* ── Paramétrage groupe ── */}
                <div className="border rounded p-3 mb-4">
                    <h5 className="mb-1">Paramétrage global (optionnel)</h5>
                    <p className="text-muted small mb-3">
                        Renseignez ce paramétrage pour l'appliquer à toutes les sociétés/sites. Sinon, paramétrez indépendamment chaque société/site.
                    </p>

                    {!groupIsActive && companyOverrides.some(co => co.sites.some(s => s.IDSignatureParametreConnexion)) && (
                        <div className="alert alert-warning py-2 small mb-3">
                            <i className="fa fa-exclamation-triangle mr-1"></i>
                            La connexion globale n'est pas active. À l'enregistrement, les connexions par site
                            seront désactivées, sauf celles dont le token reste valide (test Oodrive OK), et
                            toute société sans site valide deviendra indisponible pour la signature.
                        </div>
                    )}

                    <div className="row mb-3">
                        <div className="col-md-4">
                            <label>Représentant groupe</label>
                            <Select
                                options={userOptions}
                                value={getUserOption(groupConnexion.IDPeopleRepresentant)}
                                onChange={(option: SelectOption | null) =>
                                    onUpdateGroupConnexion('IDPeopleRepresentant', option ? option.value : undefined)
                                }
                                placeholder="Sélectionner un représentant"
                                searchable={true}
                                clearable={true}
                            />
                        </div>

                        <div className="col-md-5">
                            <label>Token groupe</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="JToken Oodrive groupe"
                                value={groupConnexion.Token || ''}
                                onChange={(e) => onUpdateGroupConnexion('Token', e.target.value)}
                            />
                        </div>

                        <div className="col-md-3 d-flex align-items-end">
                            {groupCanTest && groupConnexion.IsTokenModified && (
                                <button
                                    className={`btn btn-sm mr-2 ${getConnectionToggleClass(groupConnexion.TestStatus)}`}
                                    onClick={onTestGroupConnexion}
                                    disabled={testingGroup}
                                >
                                    {testingGroup ? (
                                        <><i className="fa fa-spinner fa-spin"></i><span className="ml-1">Test...</span></>
                                    ) : (
                                        <>
                                            Test
                                            {groupConnexion.TestStatus === 'active' && <i className="fa fa-check ml-1"></i>}
                                            {groupConnexion.TestStatus === 'error' && <i className="fa fa-times ml-1"></i>}
                                        </>
                                    )}
                                </button>
                            )}
                            <button
                                className={`btn btn-sm ${groupIsActive ? 'btn-success' : 'btn-warning'}`}
                                onClick={() => onUpdateGroupConnexion('EstActif', !groupConnexion.EstActif)}
                                disabled={!groupCanTest}
                                title={!groupCanTest ? 'Renseignez un représentant et un token' : undefined}
                            >
                                <i className={`fa ${groupIsActive ? 'fa-toggle-on' : 'fa-toggle-off'} mr-1`}></i>
                                {groupIsActive ? 'Activée' : 'Désactivée'}
                            </button>
                        </div>
                    </div>

                    <div className="form-check mb-3">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id="applyConnectionToAll"
                            checked={effectiveApplyConnectionToAll}
                            onChange={onToggleApplyConnectionToAll}
                            disabled={!groupIsActive}
                        />
                        <label className="form-check-label" htmlFor="applyConnectionToAll">
                            Appliquer le représentant et le token à tous les sites
                        </label>
                    </div>

                    <div className="mb-2 d-flex align-items-center">
                        <label className="mr-2 mb-0">Contre-signature automatique groupe</label>
                        <span
                            className="mr-3 text-muted"
                            title="Si désactivée, chaque document devra être contre-signé manuellement depuis l'interface Oodrive."
                            style={{ cursor: 'help' }}
                        >
                            <i className="fa fa-question-circle"></i>
                        </span>
                        <button
                            className={`btn btn-sm ${contreSignatureGroupe ? 'btn-success' : 'btn-warning'}`}
                            onClick={onToggleContreSignatureGroupe}
                        >
                            <i className={`fa ${contreSignatureGroupe ? 'fa-toggle-on' : 'fa-toggle-off'} mr-1`}></i>
                            {contreSignatureGroupe ? 'Activée' : 'Désactivée'}
                        </button>
                    </div>

                    <div className="form-check mt-2">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id="applyCounterSignToAll"
                            checked={applyCounterSignToAll}
                            onChange={onToggleApplyCounterSignToAll}
                        />
                        <label className="form-check-label" htmlFor="applyCounterSignToAll">
                            Appliquer la contre-signature automatique à toutes les sociétés
                        </label>
                    </div>
                </div>

                {/* ── Paramétrage sociétés/sites ── */}
                {companyOverrides.length > 0 && (
                    <>
                        <h5 className="mb-3">Paramétrage sociétés/sites</h5>
                        <div style={{ maxHeight: '50vh', overflow: 'auto' }}>
                        <table className="table table-bordered table-sm">
                            <thead className="thead-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                <tr>
                                    <th style={{ width: '14%' }}>Société</th>
                                    <th style={{ width: '16%' }}>Site</th>
                                    <th style={{ width: '18%' }}>Représentant</th>
                                    <th style={{ width: '22%' }}>Token</th>
                                    <th style={{ width: '15%' }}>
                                        Contre-signature auto{' '}
                                        <span
                                            className="text-muted"
                                            title="Si la contre-signature automatique n'est pas activée, chaque document devra être contre-signé manuellement depuis l'interface Oodrive."
                                            style={{ cursor: 'help' }}
                                        >
                                            <i className="fa fa-question-circle"></i>
                                        </span>
                                    </th>
                                    <th style={{ width: '15%' }}>
                                        <div className="d-flex align-items-center" style={{ position: 'relative' }}>
                                            <span className="mr-1">Crédits consommés</span>
                                            {contrats.length > 0 && (
                                                <div ref={bdcDropdownRef} style={{ position: 'relative' }}>
                                                    <button
                                                        className={`btn btn-sm btn-link p-0 ${selectedBdc ? 'text-primary' : 'text-muted'}`}
                                                        onClick={() => setShowBdcDropdown(v => !v)}
                                                        title="Filtrer par bon de commande"
                                                    >
                                                        <i className="fa fa-filter"></i>
                                                    </button>
                                                    {showBdcDropdown && (
                                                        <div
                                                            className="dropdown-menu show p-2"
                                                            style={{ minWidth: '180px', top: '100%', right: 0, left: 'auto', zIndex: 1000 }}
                                                        >
                                                            <div className="dropdown-header px-0 pt-0">Bon de commande</div>
                                                            <button
                                                                className={`dropdown-item px-1 ${selectedBdc === '' ? 'active' : ''}`}
                                                                onClick={() => {
                                                                    setSelectedBdc('')
                                                                    companyOverrides.forEach(co => onFilterCreditsByBdc(co.IDPlaque, ''))
                                                                    setShowBdcDropdown(false)
                                                                }}
                                                            >
                                                                Tous les BDC
                                                            </button>
                                                            {contrats.map(c => (
                                                                <button
                                                                    key={c.NoCmd}
                                                                    className={`dropdown-item px-1 ${selectedBdc === c.NoCmd ? 'active' : ''}`}
                                                                    onClick={() => {
                                                                        setSelectedBdc(c.NoCmd)
                                                                        companyOverrides.forEach(co => onFilterCreditsByBdc(co.IDPlaque, c.NoCmd))
                                                                        setShowBdcDropdown(false)
                                                                    }}
                                                                >
                                                                    {c.NoCmd}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {companyOverrides.map(company => (
                                    <React.Fragment key={company.IDPlaque}>
                                        {/* Ligne société */}
                                        <tr style={{ backgroundColor: '#e9ecef' }}>
                                            <td>
                                                <strong>
                                                    {company.NomSociete}
                                                </strong>
                                            </td>
                                            <td className="text-muted">-</td>
                                            <td className="text-muted">-</td>
                                            <td className="text-muted">-</td>
                                            <td>{renderCounterSignButton(company)}</td>
                                            <td>
                                                <strong>
                                                    {creditsByBdc[company.IDPlaque]
                                                        ? creditsByBdc[company.IDPlaque].loading
                                                            ? <i className="fa fa-spinner fa-spin"></i>
                                                            : creditsByBdc[company.IDPlaque].creditsConsommes || '—'
                                                        : company.CreditsConsommes
                                                    }
                                                </strong>
                                            </td>
                                        </tr>

                                        {/* Lignes sites */}
                                        {company.sites.map(site => {
                                            const siteKey = `${company.IDPlaque}-${site.IDSite}`
                                            const canTest = !!(site.IDPeopleRepresentant && site.Token)
                                            const siteIsActive = !!(site.EstActif && canTest)

                                            const displayRepresentant = effectiveApplyConnectionToAll
                                                ? getUserOption(groupConnexion.IDPeopleRepresentant)
                                                : getUserOption(site.IDPeopleRepresentant)

                                            const displayToken = effectiveApplyConnectionToAll
                                                ? (groupConnexion.Token || '')
                                                : (site.Token || '')

                                            return (
                                                <tr key={site.IDSite}>
                                                    <td className="text-muted">-</td>
                                                    <td>
                                                        {site.Descr}
                                                    </td>
                                                    <td>
                                                        <Select
                                                            options={userOptions}
                                                            value={displayRepresentant}
                                                            onChange={(option: SelectOption | null) =>
                                                                onUpdateSiteOverride(company.IDPlaque, site.IDSite, 'IDPeopleRepresentant', option ? option.value : undefined)
                                                            }
                                                            placeholder={effectiveApplyConnectionToAll ? '—' : 'Représentant site'}
                                                            searchable={!effectiveApplyConnectionToAll}
                                                            clearable={!effectiveApplyConnectionToAll}
                                                            disabled={effectiveApplyConnectionToAll}
                                                        />
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm mr-1"
                                                                placeholder={effectiveApplyConnectionToAll ? '—' : 'Token site'}
                                                                value={displayToken}
                                                                disabled={effectiveApplyConnectionToAll}
                                                                onChange={(e) => onUpdateSiteOverride(company.IDPlaque, site.IDSite, 'Token', e.target.value)}
                                                            />
                                                            {!effectiveApplyConnectionToAll && canTest && site.IsTokenModified && (
                                                                <button
                                                                    className={`btn btn-sm mr-1 ${getConnectionToggleClass(site.TestStatus)}`}
                                                                    onClick={() => onTestSiteConnection(company.IDPlaque, site.IDSite)}
                                                                    disabled={testingSite === siteKey}
                                                                    title="Tester la connexion"
                                                                >
                                                                    {testingSite === siteKey
                                                                        ? <i className="fa fa-spinner fa-spin"></i>
                                                                        : <>Test{site.TestStatus === 'active' && <i className="fa fa-check ml-1"></i>}{site.TestStatus === 'error' && <i className="fa fa-times ml-1"></i>}</>
                                                                    }
                                                                </button>
                                                            )}
                                                            {!effectiveApplyConnectionToAll && (
                                                                <button
                                                                    className={`btn btn-sm ${siteIsActive ? 'btn-success' : 'btn-warning'}`}
                                                                    onClick={() => onUpdateSiteOverride(company.IDPlaque, site.IDSite, 'EstActif', !site.EstActif)}
                                                                    disabled={!canTest}
                                                                    title={!canTest ? 'Renseignez un représentant et un token' : undefined}
                                                                >
                                                                    <i className={`fa ${siteIsActive ? 'fa-toggle-on' : 'fa-toggle-off'}`}></i>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="text-muted">-</td>
                                                    <td className="text-muted">-</td>
                                                </tr>
                                            )
                                        })}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}
